/* eslint-disable no-unused-vars */
"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@aichat/ui/lib/utils";
import { AssistantAvatar, Button } from "@aichat/ui";
import { useWebWidgetConfig } from "../web-widget/use-web-widget-config";
import { getHSLAString, HSLColor } from "@aichat/aichat-lib";
import { useIsFrameOnMobile } from "@aichat/ui/hooks/use-mobile";
import ThemeSleek from "./theme/sleek";

export type ChatPosition = "bottom-right" | "bottom-left";
export type ChatSize = "sm" | "md" | "lg" | "xl" | "full" | "custom" | "sleek";

const chatConfig = {
  dimensions: {
    sm: "sm:max-w-sm sm:max-h-[500px]",
    md: "sm:max-w-md sm:max-h-[600px]",
    lg: "sm:max-w-lg sm:max-h-[700px]",
    xl: "sm:max-w-xl sm:max-h-[800px]",
    custom: "sm:max-w-md sm:max-h-[800px]",
    sleek: "sm:max-w-[32rem] sm:max-h-[800px]",
    full: "sm:w-full sm:h-full",
  },
  positions: {
    "bottom-right": "bottom-3 right-3",
    "bottom-left": "bottom-3 left-3",
  },
  chatPositions: {
    "bottom-right": "sm:bottom-[calc(100%+10px)] sm:right-0",
    "bottom-left": "sm:bottom-[calc(100%+10px)] sm:left-0",
  },
  states: {
    open: "pointer-events-auto opacity-100 visible scale-100",
    closed: "pointer-events-none opacity-0 invisible scale-0",
  },
};

interface ExpandableChatProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onToggle"> {
  position?: ChatPosition;
  size?: ChatSize;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  isFirstChat: boolean;
  avatarVariant?: "light" | "dark";
  customAvatarUrl?: string;
  customAvatarUrlClassName?: string;
  expandableChatToggleClassName?: string;
  onToggle?: (open: boolean) => void;
  isVoiceMode: boolean;
}

const ExpandableChat: React.FC<ExpandableChatProps> = ({
  className,
  position = "bottom-right",
  size = "md",
  children,
  defaultOpen,
  open,
  isFirstChat,
  onToggle,
  avatarVariant,
  customAvatarUrl,
  customAvatarUrlClassName,
  expandableChatToggleClassName,
  isVoiceMode,
  ...props
}) => {
  const initialOpen = open !== undefined ? open : !!defaultOpen;
  const [isOpen, setIsOpen] = useState(initialOpen);

  const config = useWebWidgetConfig();
  const isOnMobile = useIsFrameOnMobile();

  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(initialOpen);
  }, [initialOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    onToggle?.(!isOpen);
  };

  return (
    <div
      className={cn(
        `fixed ${chatConfig.positions[position]} z-50`,
        className,
        /* Only when the state open, iframe is already handled the bottom & right position in iframe.css */
        isOpen ? "bottom-0 right-0" : "",
      )}
      {...props}
    >
      <div
        id="aichat-messenger-container"
        ref={chatRef}
        className={cn(
          "flex flex-col border-0 sm:rounded-2xl overflow-hidden ease-out sm:absolute sm:w-[90vw] sm:h-[80vh] fixed inset-0 w-full h-full sm:inset-auto",
          config.isThemeSleek
            ? "sm:h-[88.5vh] items-end"
            : "bg-background-main shadow-xl",
          chatConfig.chatPositions[position],
          chatConfig.dimensions[size],
          isOpen ? chatConfig.states.open : chatConfig.states.closed,
          config.isThemeSleek && isOnMobile ? "justify-end" : "",
          className,
        )}
        style={{
          transformOrigin: "right bottom 0",
          transitionDuration: "300ms",
          transitionProperty: "transform",
        }}
      >
        {config.isThemeSleek ? (
          <ThemeSleek
            isFirstChat={isFirstChat}
            toggleChat={toggleChat}
            isVoiceMode={isVoiceMode}
          >
            {children}
          </ThemeSleek>
        ) : (
          <>
            {children}
            <Button
              size="icon"
              className="absolute z-[999] size-7 bg-background/50 hover:bg-background rounded-full top-4 right-4"
              onClick={toggleChat}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
      <ExpandableChatToggle
        customAvatarUrl={customAvatarUrl}
        customAvatarUrlClassName={customAvatarUrlClassName}
        className={expandableChatToggleClassName}
        isOpen={isOpen}
        avatarVariant={avatarVariant}
        toggleChat={toggleChat}
      />
    </div>
  );
};

ExpandableChat.displayName = "ExpandableChat";

const ExpandableChatHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex items-center justify-between p-4 border-b", className)}
    {...props}
  />
);

ExpandableChatHeader.displayName = "ExpandableChatHeader";

const ExpandableChatBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-grow overflow-y-auto", className)}
    {...props}
  />
));

ExpandableChatBody.displayName = "ExpandableChatBody";

const ExpandableChatFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn("border-t p-4", className)} {...props} />;

ExpandableChatFooter.displayName = "ExpandableChatFooter";

type ExpandableChatToggleProps = {
  customAvatarUrl?: string;
  customAvatarUrlClassName?: string;
  isOpen: boolean;
  toggleChat?: () => void;
  avatarVariant?: "light" | "dark";
  brandColor?: HSLColor;
  brandColorAsBgLauncher?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const ExpandableChatToggle: React.FC<ExpandableChatToggleProps> = ({
  className,
  customAvatarUrl,
  customAvatarUrlClassName,
  isOpen,
  toggleChat,
  avatarVariant,
  brandColor: brandColorProps,
  brandColorAsBgLauncher: brandColorAsBgLauncherProps,
  ...props
}) => {
  const [toggling, setToggling] = useState(isOpen);
  const { brandColor, brandColorAsBgLauncher } = useWebWidgetConfig();

  let timeout: ReturnType<typeof setTimeout>;

  const handleToggle = () => {
    if (!toggleChat) return;
    clearTimeout(timeout);
    setToggling(true);
    toggleChat();
    timeout = setTimeout(() => {
      setToggling(false);
    }, 300);
  };

  const currentBrandColor =
    brandColorProps !== undefined ? brandColorProps : brandColor;
  const currentBrandColorAsLauncherBg =
    brandColorAsBgLauncherProps !== undefined
      ? brandColorAsBgLauncherProps
      : brandColorAsBgLauncher;
  const brandColorHSLA = getHSLAString(currentBrandColor);

  const shouldScale0 = (isOpen && !toggling) || toggling;

  return (
    <Button
      id="aichat-messenger-launcher"
      variant="default"
      size="icon"
      onClick={handleToggle}
      className={cn(
        "w-14 h-14 rounded-full px-0 py-0 shadow-md flex items-center justify-center hover:shadow-lg hover:shadow-black/30 duration-300 relative hover:scale-105 scale-0 opacity-0",
        className,
        {
          "scale-100 opacity-100 transition-all": !isOpen,
          "!shadow-none": !currentBrandColorAsLauncherBg,
        },
      )}
      {...props}
      style={{
        ...props.style,
        border: currentBrandColorAsLauncherBg
          ? `3px solid ${brandColorHSLA}`
          : "none",
        backgroundColor: currentBrandColorAsLauncherBg
          ? brandColorHSLA
          : "unset",
      }}
    >
      <style>
        {`
          @keyframes ripple {
            0% {
              ${currentBrandColorAsLauncherBg ? "" : "scale:0;"}
              box-shadow: ${brandColorHSLA} 0px 0px 0px 0px;
            }
            100% {
              ${currentBrandColorAsLauncherBg ? "" : "scale:0.9;"}
              box-shadow:rgba(0, 0, 0, 0) 0px 0px 0px 10px;
            }
          }
          .widget-ripple-effect {
            animation: ripple 1.5s infinite ease-out;
          }
        `}
      </style>
      <div className="absolute z-10 inset-0 h-full w-full rounded-full widget-ripple-effect"></div>
      <ChevronUp
        className={cn("transition-all duration-300 !size-8 absolute", {
          "rotate-180": isOpen,
          "rotate-0 opacity-0": !isOpen && !toggling,
          "text-white": avatarVariant === "light",
          "text-black": avatarVariant === "dark",
        })}
      />

      <AssistantAvatar
        avatarType="bot"
        customUrl={customAvatarUrl}
        customUrlClassName={cn(customAvatarUrlClassName, {
          "scale-0": shouldScale0,
          "z-20": !currentBrandColorAsLauncherBg,
        })}
        variant={avatarVariant}
        className={cn("!size-9 absolute transition-all duration-300", {
          "scale-0": shouldScale0,
        })}
      />
    </Button>
  );
};

ExpandableChatToggle.displayName = "ExpandableChatToggle";

export {
  ExpandableChat,
  ExpandableChatHeader,
  ExpandableChatBody,
  ExpandableChatFooter,
};
